import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/lib/admin-guard";
import {
  listLeadConversions,
  type LeadRow,
} from "@/lib/lead-conversion.functions";
import {
  ArrowLeft,
  Search,
  Download,
  TrendingUp,
  CheckCircle2,
  Users,
  CreditCard,
  Sparkles,
} from "lucide-react";

const searchSchema = z.object({
  filter: z
    .enum(["all", "leads_only", "active_members", "pending_payments", "refunded", "cancelled"])
    .optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute("/admin_/lead-conversion")({
  ssr: false,
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Lead Conversion — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: LeadConversionPage,
});

function fmtMoney(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency ?? "USD").toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency ?? ""}`.trim();
  }
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function csvEscape(v: unknown) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

const FILTERS: Array<{ key: NonNullable<z.infer<typeof searchSchema>["filter"]>; label: string }> = [
  { key: "all", label: "All" },
  { key: "leads_only", label: "Leads only" },
  { key: "active_members", label: "Active Members" },
  { key: "pending_payments", label: "Pending Payments" },
  { key: "refunded", label: "Refunded" },
  { key: "cancelled", label: "Cancelled" },
];

function StatusBadge({ status }: { status: LeadRow["display_membership_status"] }) {
  const cls: Record<LeadRow["display_membership_status"], string> = {
    "Lead": "bg-muted text-foreground/70",
    "Pending Payment": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    "Active Member": "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    "Cancelled": "bg-slate-500/15 text-foreground dark:text-slate-300",
    "Refunded": "bg-destructive/10 text-destructive",
    "Registered (Unpaid)": "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    "Unknown": "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${cls[status]}`}>
      {status}
    </span>
  );
}

function PaymentBadge({ status }: { status: LeadRow["payment_status"] }) {
  if (status === "—") return <span className="text-xs text-muted-foreground">—</span>;
  const cls: Record<Exclude<LeadRow["payment_status"], "—">, string> = {
    Paid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    Pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    Failed: "bg-destructive/10 text-destructive",
    Refunded: "bg-destructive/10 text-destructive",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls[status]}`}>
      {status}
    </span>
  );
}

function LeadConversionPage() {
  const sp = Route.useSearch();
  const navigate = Route.useNavigate();
  const [q, setQ] = useState(sp.q ?? "");
  const filter = sp.filter ?? "all";

  const fetcher = useServerFn(listLeadConversions);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "lead-conversion", filter, sp.q],
    queryFn: () => fetcher({ data: { filter, search: sp.q || undefined } }),
  });

  const rows = data?.rows ?? [];
  const metrics = data?.metrics;

  const setFilter = (f: NonNullable<z.infer<typeof searchSchema>["filter"]>) => {
    navigate({ search: { ...(sp.q ? { q: sp.q } : {}), filter: f === "all" ? undefined : f } });
  };
  const applySearch = () => {
    navigate({
      search: {
        ...(filter !== "all" ? { filter } : {}),
        q: q.trim() || undefined,
      },
    });
  };

  function exportCsv() {
    const header = [
      "full_name",
      "email",
      "waitlist_joined_at",
      "account_created_at",
      "membership_status",
      "payment_status",
      "founding_member",
      "stripe_customer_id",
      "membership_started_at",
      "next_billing_date",
      "last_payment",
      "ltv",
      "currency",
      "converted",
    ];
    const lines = rows.map((r) => [
      r.full_name ?? "",
      r.email,
      r.waitlist_joined_at ?? "",
      r.account_created_at ?? "",
      r.display_membership_status,
      r.payment_status,
      r.is_founding_member ? "Yes" : "No",
      r.stripe_customer_id ?? "",
      r.membership_started_at ?? "",
      r.next_billing_date ?? "",
      r.last_payment_cents != null ? (r.last_payment_cents / 100).toFixed(2) : "",
      r.ltv_cents != null ? (r.ltv_cents / 100).toFixed(2) : "",
      r.currency ?? "",
      r.converted ? "Yes" : "No",
    ]);
    const csv =
      "\uFEFF" +
      [header, ...lines].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-conversion-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            <TrendingUp className="h-5 w-5 text-foreground" strokeWidth={2.25} />
          </div>
          <div>
            <h1 className="font-display text-3xl text-foreground">Lead Conversion</h1>
            <p className="text-sm text-muted-foreground">
              Track waiting-list leads through to paying Founding Members.
            </p>
          </div>
        </div>
        <button
          onClick={exportCsv}
          disabled={!rows.length}
          className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card px-4 py-2 text-sm font-semibold hover:bg-gold/10 disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </header>

      {/* Metrics */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        <MetricCard icon={<Users className="h-4 w-4" />} label="Total Leads" value={metrics?.total_leads ?? "—"} />
        <MetricCard icon={<Users className="h-4 w-4" />} label="Accounts Created" value={metrics?.accounts_created ?? "—"} />
        <MetricCard icon={<Sparkles className="h-4 w-4" />} label="Active Founding" value={metrics?.active_founding_members ?? "—"} />
        <MetricCard icon={<TrendingUp className="h-4 w-4" />} label="Lead → Member" value={metrics ? `${metrics.conversion_rate}%` : "—"} />
        <MetricCard
          icon={<CreditCard className="h-4 w-4" />}
          label="Total Revenue"
          value={metrics ? fmtMoney(metrics.total_revenue_cents, metrics.revenue_currency) : "—"}
        />
        <MetricCard label="Refunded" value={metrics?.refunded_members ?? "—"} />
        <MetricCard label="Cancelled" value={metrics?.cancelled_members ?? "—"} />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              filter === f.key
                ? "border-transparent bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]"
                : "border-gold/30 bg-card text-foreground/80 hover:bg-gold/10"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ms-auto min-w-[240px] flex-1 sm:flex-initial">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            onBlur={applySearch}
            placeholder="Search name or email…"
            className="w-full rounded-full border border-gold/30 bg-background py-2 ps-9 pe-3 text-sm"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gold/20 bg-card">
        {error ? (
          <p className="p-6 text-sm text-destructive">
            {(error as Error).message || "Couldn't load lead conversion data."}
          </p>
        ) : isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            No records match this filter.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gold/10 text-start text-xs uppercase tracking-wider text-foreground/70">
                <tr>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Email</th>
                  <th className="px-3 py-3">Waitlist joined</th>
                  <th className="px-3 py-3">Account created</th>
                  <th className="px-3 py-3">Membership</th>
                  <th className="px-3 py-3">Payment</th>
                  <th className="px-3 py-3">Founding</th>
                  <th className="px-3 py-3">Stripe customer</th>
                  <th className="px-3 py-3">Started</th>
                  <th className="px-3 py-3">Next billing</th>
                  <th className="px-3 py-3 text-end">Last payment</th>
                  <th className="px-3 py-3 text-end">LTV</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.email} className="border-t border-border/30 align-top">
                    <td className="px-3 py-3 font-medium text-foreground whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>{r.full_name ?? "—"}</span>
                        {r.converted && (
                          <span
                            title="Converted from lead to paying member"
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300"
                          >
                            <CheckCircle2 className="h-3 w-3" /> Converted
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-foreground break-all">{r.email}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.waitlist_joined_at)}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.account_created_at)}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={r.display_membership_status} />
                    </td>
                    <td className="px-3 py-3">
                      <PaymentBadge status={r.payment_status} />
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {r.is_founding_member ? (
                        <span className="rounded-full bg-gold-light/60 px-2 py-0.5 text-[var(--gold-deep)] font-semibold">Yes</span>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[11px] text-muted-foreground font-mono break-all">
                      {r.stripe_customer_id ?? "—"}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.membership_started_at)}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.next_billing_date)}
                    </td>
                    <td className="px-3 py-3 text-xs text-end whitespace-nowrap">
                      {fmtMoney(r.last_payment_cents, r.currency)}
                    </td>
                    <td className="px-3 py-3 text-xs text-end whitespace-nowrap font-semibold">
                      {fmtMoney(r.ltv_cents, r.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">
        LTV is calculated from the last recorded payment on each membership. For full multi-cycle history, cross-reference Stripe directly.
      </p>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display text-xl text-foreground">{value}</div>
    </div>
  );
}
